/**
 * clickNotDrag — "a drag is not a click" — Card P95 item 3.
 *
 * ⛔⛔ WHY THIS EXISTS. A kanban card is both draggable and clickable, and the
 * browser fires `click` at the end of a drag that started and ended on the same
 * element. Opening a slide-over there means every abandoned drag — or every
 * drag that lands back in its own column — throws a drawer over the board the
 * rep was using. The card looks broken in exactly the moment the rep is being
 * most deliberate.
 *
 * ⚠⚠ AND IT IS NOT ENOUGH TO WATCH `dragstart`. A drag that is begun and
 * abandoned fires `dragstart` with no `dragend` on some paths, and a *slow*
 * press that moves a few pixels fires no drag events at all while still not
 * being a click the rep meant. So this watches BOTH: a drag was started, OR
 * the pointer travelled further than a thumb's wobble between down and up.
 *
 * ⚠ THRESHOLD, NOT ZERO. A real click moves a pixel or two — a mouse shifts
 * under the finger, and a trackpad tap is noisier than that. Requiring exact
 * stillness would reject ordinary clicks, which is the opposite failure and a
 * far more annoying one.
 *
 * Usage:
 *   const drag = useClickNotDrag();
 *   <div {...drag.handlers} onClick={drag.guard(() => open(id))} draggable … />
 */

/** Pixels the pointer may travel between down and up and still be a click. */
export const CLICK_SLOP_PX = 5;

/**
 * Framework-free core, so it can be unit-tested without a renderer and reused
 * by a non-React caller.
 */
export function createClickNotDrag({ slop = CLICK_SLOP_PX } = {}) {
  let startX = null;
  let startY = null;
  let dragged = false;

  const onPointerDown = (e) => {
    startX = e && typeof e.clientX === 'number' ? e.clientX : null;
    startY = e && typeof e.clientY === 'number' ? e.clientY : null;
    dragged = false;
  };
  const onDragStart = () => { dragged = true; };
  /*
   * ⚠ `dragend` does NOT clear the flag. The `click` that a drag produces
   * arrives AFTER dragend on every browser this app supports, so clearing here
   * would un-suppress the very click this guard exists to swallow. The flag is
   * cleared on the next pointerdown instead — the start of the next gesture.
   */
  const onDragEnd = () => {};

  /** Did this gesture move far enough to be a drag rather than a click? */
  const movedTooFar = (e) => {
    if (startX == null || startY == null) return false;
    if (!e || typeof e.clientX !== 'number' || typeof e.clientY !== 'number') return false;
    return Math.abs(e.clientX - startX) > slop || Math.abs(e.clientY - startY) > slop;
  };

  return {
    handlers: { onPointerDown, onDragStart, onDragEnd },
    /** Wrap a click handler so it runs only for a genuine click. */
    guard(fn) {
      return (e) => {
        const wasDrag = dragged || movedTooFar(e);
        dragged = false;
        if (wasDrag) return undefined;
        return fn ? fn(e) : undefined;
      };
    },
    /** Exposed for tests and for callers that do their own dispatch. */
    isDrag(e) { return dragged || movedTooFar(e); },
  };
}

export default createClickNotDrag;
